// src/data/resourcesData.js
export const categories = [
  { id: "all", name: "All Resources" },
  { id: "communication", name: "Communication" },
  { id: "conflict", name: "Conflict Resolution" },
  { id: "growth", name: "Personal Growth" },
  { id: "transitions", name: "Relationship Transitions" },
];

export const typeIcons = {
  guide: "fa-book",
  article: "fa-file-alt",
  workbook: "fa-tasks",
  checklist: "fa-check-square",
};

export const resources = [
  {
    id: "communication-techniques",
    title: "Effective Communication Techniques",
    description:
      "Learn research-backed methods to improve how you express needs and listen to your partner.",
    category: "communication",
    type: "guide",
    image: "/images/resource-communication.jpeg",
    featured: true,
    content: [
      {
        heading: "Overview",
        paragraphs: [
          "Effective communication is the foundation of any healthy relationship. It involves both expressing your own needs clearly and hearing your partner without judgment.",
          "In this guide we’ll walk through several techniques—“I” statements, reflective listening, timing, and environment—that help conversations stay constructive.",
        ],
      },
      {
        heading: "Key Techniques",
        paragraphs: [
          "1. “I” Statements: Frame feelings around your own experience (“I feel…”), rather than casting blame.  \n" +
            "Example: Instead of “You never help around the house,” try “I feel overwhelmed when chores pile up.”",
          "2. Reflective Listening: After your partner speaks, paraphrase back what you heard and ask for clarification.  \n" +
            "Example: “So you’re saying you felt upset when I didn’t call?”",
          "3. Timing & Environment: Choose a neutral time and private space. Avoid urgent confrontations when either person is stressed or distracted.",
        ],
      },
      {
        heading: "Exercises to Try",
        paragraphs: [
          "• Daily Check-Ins: Spend 5 minutes at dinner each night practicing reflective listening.  \n" +
            "• Issue “I” Statement Drill: Write down three issues, convert each into an “I” statement, and swap with your partner for feedback.",
        ],
      },
      {
        heading: "Further Reading",
        paragraphs: [
          "• John Gottman, “The Seven Principles for Making Marriage Work”  \n" +
            "• Marshall Rosenberg, “Nonviolent Communication: A Language of Life”",
        ],
      },
    ],
  },
  {
    id: "nonviolent-communication",
    title: "Nonviolent Communication in Relationships",
    description:
      "A practical approach to speaking and listening that leads to giving and receiving compassionately.",
    category: "communication",
    type: "article",
    image: "/images/resource-nonviolent.jpeg",
    featured: false,
    content: [
      {
        heading: "What Is NVC?",
        paragraphs: [
          "Nonviolent Communication (NVC) is a process developed by Marshall Rosenberg to foster empathy and compassionate connection. It breaks communication into four components:",
        ],
      },
      {
        heading: "The Four Components",
        paragraphs: [
          "1. Observations (vs. evaluations): Describe what you see/hear without judgment.  \n" +
            "2. Feelings: Name your emotions clearly.  \n" +
            "3. Needs: Identify underlying needs driving those feelings.  \n" +
            "4. Requests: Ask for concrete actions to enrich life.",
        ],
      },
      {
        heading: "Applying NVC in Daily Life",
        paragraphs: [
          "• Practice distinguishing observations from judgments (e.g., “When dishes pile up…” vs. “You’re so lazy”).  \n" +
            "• Share feelings & needs in low-stakes moments.  \n" +
            "• Make clear, doable requests (“Could you load the dishwasher tonight?”).",
        ],
      },
      {
        heading: "Benefits",
        paragraphs: [
          "NVC reduces defensiveness, increases mutual understanding, and often leads to win-win solutions.",
        ],
      },
    ],
  },
  {
    id: "conflict-resolution",
    title: "Healthy Conflict Resolution",
    description:
      "Convert disagreements into opportunities for growth and deeper understanding.",
    category: "conflict",
    type: "guide",
    image: "/images/resource-conflict.jpeg",
    featured: true,
    content: [
      {
        heading: "Why Conflict Can Be Good",
        paragraphs: [
          "Disagreements are natural. Handled well, they help partners learn more about each other’s values and needs.",
        ],
      },
      {
        heading: "Five Steps to Resolve Conflict",
        paragraphs: [
          "1. Define the Issue: Each person states their view in neutral terms.  \n" +
            "2. Express Feelings: Use “I feel…” language.  \n" +
            "3. Brainstorm Solutions: No criticism—just ideas.  \n" +
            "4. Agree on Action: Choose one solution and set a time to revisit.  \n" +
            "5. Follow Up: Check in after a few days to see how it’s going.",
        ],
      },
      {
        heading: "Tips for Staying Calm",
        paragraphs: [
          "• Take a 5-minute time-out if voices rise.  \n" +
            "• Focus on the present issue—avoid “kitchen sinking” (bringing up past hurts).  \n" +
            "• Keep a respectful tone; watch body language.",
        ],
      },
    ],
  },
  {
    id: "fair-fighting",
    title: "Fair Fighting Rules",
    description:
      "Guidelines for handling disagreements respectfully and productively.",
    category: "conflict",
    type: "checklist",
    image: "/images/resource-fair-fighting.jpeg",
    featured: false,
    content: [
      {
        heading: "The Fair-Fighting Checklist",
        paragraphs: [
          "• No name-calling or insults.  \n" +
            "• Stick to one issue at a time.  \n" +
            "• Avoid bringing up old arguments.  \n" +
            "• Don’t use absolutes (“You always…” / “You never…”).  \n" +
            "• Use “I feel…” statements.  \n" +
            "• Take breaks when heated.  \n" +
            "• End on a positive note or agreement to reconvene.",
        ],
      },
      {
        heading: "Why It Matters",
        paragraphs: [
          "Fair-fighting keeps respect intact and ensures both partners feel heard—even when you disagree.",
        ],
      },
    ],
  },
  {
    id: "self-worth",
    title: "Building Self-Worth",
    description:
      "Develop a strong sense of self-value independent of relationship status.",
    category: "growth",
    type: "workbook",
    image: "/images/resource-self-worth.jpeg",
    featured: true,
    content: [
      {
        heading: "Understanding Self-Worth",
        paragraphs: [
          "Self-worth comes from recognizing your intrinsic value—beyond achievements or how others treat you.",
        ],
      },
      {
        heading: "Exercises",
        paragraphs: [
          "1. Daily Affirmations: Write down three qualities you like about yourself each morning.  \n" +
            "2. Journal Prompt: “When did I feel most alive this week, and why?”  \n" +
            "3. Boundary Mapping: Identify one area you need a stronger “no.”",
        ],
      },
      {
        heading: "Next Steps",
        paragraphs: [
          "Review your affirmations weekly, track progress, and celebrate small victories in self-care.",
        ],
      },
    ],
  },
  {
    id: "attachment-styles",
    title: "Understanding Attachment Styles",
    description:
      "How childhood experiences shape adult relationship patterns and what you can do about it.",
    category: "growth",
    type: "article",
    image: "/images/resource-attachment.jpeg",
    featured: true,
    content: [
      {
        heading: "The Four Styles",
        paragraphs: [
          "1. Secure: Comfortable with intimacy and autonomy.  \n" +
            "2. Anxious: Worries about partner’s availability.  \n" +
            "3. Avoidant: Restricts closeness, values independence.  \n" +
            "4. Fearful-Avoidant: Desires closeness but fears getting hurt.",
        ],
      },
      {
        heading: "How Styles Form",
        paragraphs: [
          "Early caregiver responsiveness shapes your attachment. Secure kids grew up with consistent care; anxious/avoidant styles form when needs were unpredictably met or dismissed.",
        ],
      },
      {
        heading: "Moving Toward Security",
        paragraphs: [
          "• Self-awareness: Notice triggers (e.g., partner out late → anxiety).  \n" +
            "• Communication: Share attachment needs openly.  \n" +
            "• Therapy or workshops: Practice secure attachment behaviors in a safe setting.",
        ],
      },
    ],
  },
  {
    id: "breakup-recovery",
    title: "Healing After a Breakup",
    description:
      "A step-by-step guide to processing grief and rebuilding after a relationship ends.",
    category: "transitions",
    type: "guide",
    image: "/images/resource-breakup.jpeg",
    featured: true,
    content: [
      {
        heading: "Allow Yourself to Grieve",
        paragraphs: [
          "Breakups can trigger loss, sadness, even relief. Embrace your emotions without judgment.",
        ],
      },
      {
        heading: "Seven-Stage Recovery",
        paragraphs: [
          "1. Shock & Denial  \n" +
            "2. Pain & Guilt  \n" +
            "3. Anger & Bargaining  \n" +
            "4. Depression, Wellness  \n" +
            "5. The Upward Turn  \n" +
            "6. Reconstruction & Working Through  \n" +
            "7. Acceptance & Hope",
        ],
      },
      {
        heading: "Self-Care Practices",
        paragraphs: [
          "• Daily walks or exercise  \n" +
            "• Journaling feelings  \n" +
            "• Reaching out to friends/family  \n" +
            "• Creative outlets (music, art)",
        ],
      },
    ],
  },
  {
    id: "setting-boundaries",
    title: "Setting Healthy Boundaries",
    description:
      "Learn to establish and maintain boundaries that honor your needs while respecting others.",
    category: "growth",
    type: "workbook",
    image: "/images/resource-boundaries.jpeg",
    featured: false,
    content: [
      {
        heading: "Why Boundaries Matter",
        paragraphs: [
          "Boundaries define how you want to be treated. Good boundaries foster respect and prevent resentment.",
        ],
      },
      {
        heading: "Steps to Set Boundaries",
        paragraphs: [
          "1. Identify Your Limits (emotional, physical, time).  \n" +
            "2. Be Clear & Direct (“I need 30 minutes alone after work”).  \n" +
            "3. State Consequences (“If we keep talking past 10pm, I will turn off my phone”).  \n" +
            "4. Follow Through Consistently.",
        ],
      },
      {
        heading: "Workbook Exercises",
        paragraphs: [
          "• List three situations this week where you felt your needs weren’t respected.  \n" +
            "• Rewrite each situation with a boundary statement you could have used.",
        ],
      },
    ],
  },
  {
    id: "red-flags",
    title: "Recognizing Relationship Red Flags",
    description:
      "Learn to identify warning signs of unhealthy or potentially abusive relationships early.",
    category: "transitions",
    type: "checklist",
    image: "/images/resource-red-flags.jpeg",
    featured: false,
    content: [
      {
        heading: "Common Red Flags",
        paragraphs: [
          "• Controlling behavior (who you see, wear, do).  \n" +
            "• Isolation from friends/family.  \n" +
            "• Verbal insults or humiliation.  \n" +
            "• Gaslighting (denying your experience).  \n" +
            "• Extreme jealousy or possessiveness.  \n" +
            "• Inconsistent apologies or repeated cycles of abuse.",
        ],
      },
      {
        heading: "If You Spot a Red Flag",
        paragraphs: [
          "1. Trust your gut—don’t minimize your feelings.  \n" +
            "2. Talk to a supportive friend or counselor.  \n" +
            "3. Create a safety plan if needed (emergency contacts, safe places).",
        ],
      },
    ],
  },
  {
    id: "active-listening",
    title: "The Art of Active Listening",
    description:
      "Transform your conversations by truly hearing what your partner is communicating.",
    category: "communication",
    type: "guide",
    image: "/images/resource-listening.jpeg",
    featured: false,
    content: [
      {
        heading: "What Is Active Listening?",
        paragraphs: [
          "Beyond just hearing words, active listening involves empathy, reflection, and nonverbal cues that show you’re truly engaged.",
        ],
      },
      {
        heading: "Four Core Skills",
        paragraphs: [
          "1. Pay Attention: Put away distractions, make eye contact.  \n" +
            "2. Show That You’re Listening: Nod, use small verbal affirmations (“mm-hmm,” “I see…”).  \n" +
            "3. Reflect & Clarify: Paraphrase and ask open-ended questions.  \n" +
            "4. Respond Appropriately: Share empathy, avoid unsolicited advice unless asked.",
        ],
      },
      {
        heading: "Practice Exercise",
        paragraphs: [
          "Set a 5-minute timer. One partner speaks about any topic, the other only listens, reflects, and asks questions—then swap roles.",
        ],
      },
    ],
  },
  {
    id: "love-languages",
    title: "The Five Love Languages",
    description:
      "Discover how you and your partner prefer to give and receive love for deeper connection.",
    category: "communication",
    type: "article",
    image: "/images/resource-love-languages.jpeg",
    featured: false,
    content: [
      {
        heading: "Gary Chapman’s Five Languages",
        paragraphs: [
          "1. Words of Affirmation  \n" +
            "2. Quality Time  \n" +
            "3. Receiving Gifts  \n" +
            "4. Acts of Service  \n" +
            "5. Physical Touch",
        ],
      },
      {
        heading: "How to Use Them",
        paragraphs: [
          "• Take the official quiz (online) to identify primary languages.  \n" +
            "• Discuss each other’s results.  \n" +
            "• Make small consistent gestures in your partner’s top two languages.",
        ],
      },
    ],
  },
  {
    id: "moving-forward",
    title: "Moving Forward After Infidelity",
    description:
      "Guidance for both partners on healing and rebuilding trust when possible.",
    category: "transitions",
    type: "workbook",
    image: "/images/resource-infidelity.jpeg",
    featured: false,
    content: [
      {
        heading: "The Path to Rebuilding Trust",
        paragraphs: [
          "Infidelity can shatter trust, but some couples choose to heal together. Both partners must commit to open communication and accountability.",
        ],
      },
      {
        heading: "Workbook Exercises",
        paragraphs: [
          "1. Honest Inventory: The unfaithful partner writes out motives, context, and regrets.  \n" +
            "2. Safe-Space Dialogues: Schedule 30-minute check-ins where feelings are shared without interruption.  \n" +
            "3. Action Plan: Outline daily rituals to rebuild transparency (e.g., shared calendar, check-ins).",
        ],
      },
      {
        heading: "When to Seek Help",
        paragraphs: [
          "Qualified couples therapy or support groups can provide structured guidance and neutral ground for these tough conversations.",
        ],
      },
    ],
  },
];
